'use strict';
/*
  mp4.js — finalise a MediaRecorder capture into a standard, "faststart" MP4 that
  iOS Photos will import (the recorder emits a *fragmented* MP4 / WebM, which
  Photos rejects → AirDrop drops it into Files instead of offering Save to Photos).

  Uses ffmpeg.wasm's SINGLE-THREAD core, loaded lazily from a CDN on first export.
  Single-thread → no SharedArrayBuffer → no COOP/COEP headers needed (which would
  break the cross-origin fonts / accounts CORS). If anything fails, the caller
  falls back to the raw recording, so export never breaks.
*/
// The small loader/worker/glue are vendored SAME-ORIGIN under /ffmpeg/ so the
// FFmpeg worker (a classic worker resolved relative to ffmpeg.js) can importScripts
// the core — passing classWorkerURL would make it a *module* worker and break that.
// Only the ~30 MB wasm stays on the CDN (over Cloudflare Pages' 25 MB file cap),
// loaded cross-origin via CORS.
const CORE_WASM = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm';

function loadScript(src) {
  return new Promise((res, rej) => {
    if ([...document.scripts].some(s => s.src && s.src.endsWith(src))) return res();
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => res();
    s.onerror = () => rej(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

let _ff = null, _onProgress = null;
async function getFFmpeg() {
  if (_ff) return _ff;
  await loadScript('/ffmpeg/ffmpeg.js'); // → self.FFmpegWASM (+ resolves worker same-origin)
  await loadScript('/ffmpeg/util.js');   // → self.FFmpegUtil
  const { FFmpeg } = self.FFmpegWASM;
  const ff = new FFmpeg();
  ff.on('progress', e => { if (_onProgress && e && typeof e.progress === 'number') _onProgress(Math.max(0, Math.min(1, e.progress))); });
  await ff.load({
    coreURL: location.origin + '/ffmpeg/ffmpeg-core.js',
    wasmURL: CORE_WASM,
  });
  _ff = ff;
  return ff;
}

// blob (recorder output) -> a faststart video/mp4 Blob. Throws on any failure.
export async function toShareableMp4(blob, onProgress) {
  const ff = await getFFmpeg();
  const { fetchFile } = self.FFmpegUtil;
  _onProgress = onProgress || null;
  const isMp4 = (blob.type || '').includes('mp4');
  const inName = isMp4 ? 'in.mp4' : 'in.webm';
  await ff.writeFile(inName, await fetchFile(blob));
  // Already H.264/AAC inside an MP4 → just relayout to faststart (no re-encode, fast).
  // WebM → transcode to H.264/AAC.
  const args = isMp4
    ? ['-i', inName, '-c', 'copy', '-movflags', '+faststart', 'out.mp4']
    : ['-i', inName, '-c:v', 'libx264', '-preset', 'veryfast', '-pix_fmt', 'yuv420p', '-c:a', 'aac', '-movflags', '+faststart', 'out.mp4'];
  await ff.exec(args);
  const data = await ff.readFile('out.mp4');
  _onProgress = null;
  return new Blob([data.buffer], { type: 'video/mp4' });
}
